import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Typography,
  alpha,
  useTheme,
  Fade,
  Grow,
  Slide,
  Skeleton,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DateRangeIcon from "@mui/icons-material/DateRange";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import StarIcon from "@mui/icons-material/Star";

import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import bgBanner from "../../../assets/db_gif.gif";
import MeetingCard from "../../../widgets/MeetingCard";
import { UserContext } from "../../context/UserContext";
import NotificationsWidget from "../common/NotificationsWidget";
import { getUser } from "../../../utility/utility";
import { RecommendedExhibitors } from "../exhibitor/RecommendedExhibitors";
import "react-circular-progressbar/dist/styles.css";
import useApiEp from "../../../hooks/useApiEp";

const StatCard = ({ title, count, color, icon, link, isLoading }) => {
  const theme = useTheme();

  return (
    <Grow in timeout={300}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid",
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0px 12px 24px rgba(0, 0, 0, 0.04)",
            borderColor: alpha(color || theme.palette.primary.main, 0.3),
            "& .stat-icon-box": {
              transform: "scale(1.08)",
              backgroundColor: alpha(color || theme.palette.primary.main, 0.12),
            },
          },
        }}
      >
        <CardActionArea
          component={Link}
          to={link}
          disabled={isLoading}
          sx={{ p: 3, height: "100%" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  display: "block",
                  mb: 0.5,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </Typography>

              {isLoading ? (
                <Skeleton
                  variant="text"
                  width="60%"
                  height={48}
                  sx={{ borderRadius: 1 }}
                />
              ) : (
                <Typography
                  variant="h4"
                  fontWeight={850}
                  sx={{
                    color: theme.palette.text.primary,
                    letterSpacing: "-0.5px",
                    lineHeight: 1.2,
                  }}
                >
                  {count?.toLocaleString() || 0}
                </Typography>
              )}
            </Box>

            <Box
              className="stat-icon-box"
              sx={{
                width: 52,
                height: 52,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(
                  color || theme.palette.primary.main,
                  0.08
                ),
                transition: "all 0.3s ease",
                flexShrink: 0,
                ml: 2,
              }}
            >
              {icon}
            </Box>
          </Box>
        </CardActionArea>
      </Card>
    </Grow>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const { data: viewCount, isLoading: isViewCountLoading } = useApiEp(
    `/user/my-views-count?`
  );
  const { data: pcData, isLoading: isPcloading } = useApiEp(
    `/user/visitor-profile-completion`
  );
  const { data: meetingsData, isLoading: isMloading } =
    useApiEp(`/user/get-meetings`);
  const { data: mutualFavs, isLoading: isMutualLoading } = useApiEp(
    `/user/get-mutual-likes-count`
  );

  const onSelect = (meetingItem) => {
    navigate(`/meeting/${meetingItem._id}`);
  };

  const userRole = user?.role || "visitor";

  const statsConfigs = [
    {
      title: "Profile Views",
      count: !isViewCountLoading ? viewCount?.count : null,
      color: "#fb4d24",
      icon: <VisibilityIcon sx={{ fontSize: 28, color: "#fb4d24" }} />,
      link: `/${userRole}/profile-viewers`,
      isLoading: isViewCountLoading,
    },
    {
      title: "Meeting Requests",
      count: !isMloading ? meetingsData?.length : null,
      color: "#004490",
      icon: <DateRangeIcon sx={{ fontSize: 28, color: "#004490" }} />,
      link: `/${userRole}/meetings`,
      isLoading: isMloading,
    },
    {
      title: "Mutual Connections",
      count: !isMutualLoading ? mutualFavs?.count : null,
      color: "#539506",
      icon: <GroupAddIcon sx={{ fontSize: 28, color: "#539506" }} />,
      link: `/${userRole}/mutual-connections`,
      isLoading: isMutualLoading,
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5 }}>
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{ color: "#0f172a", letterSpacing: "-0.02em" }}
          >
            Dashboard
          </Typography>
        </Box>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontWeight: 400 }}
        >
          Real-time insights and analytics for your event presence
        </Typography>
      </Box>

      {/* Welcome Banner & Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {/* Main Welcome Banner */}
        <Grid item xs={12} lg={8}>
          <Slide direction="right" in timeout={500}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                position: "relative",
                overflow: "hidden",
                height: "100%",
                minHeight: 180,
                cursor: "pointer",
                border: "1px solid rgba(255,255,255,0.1)",
                "&:hover .banner-overlay": {
                  transform: "scale(1.08)",
                },
                "&:hover .banner-gradient": {
                  opacity: 0.8,
                },
              }}
            >
              {/* Background Image */}
              <Box
                className="banner-overlay"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(${bgBanner})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  transition: "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              />

              {/* Gradient Overlay */}
              <Box
                className="banner-gradient"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(15, 23, 42, 0.92) 0%, rgba(15, 23, 42, 0.4) 60%, rgba(15, 23, 42, 0.1) 100%)",
                  transition: "opacity 0.5s ease",
                }}
              />

              {/* Animated Accent Line */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 6,
                  height: "100%",
                  background:
                    "linear-gradient(180deg, #6366f1, #8b5cf6, #a855f7)",
                  zIndex: 3,
                }}
              />

              {/* Content */}
              <CardContent
                sx={{
                  position: "relative",
                  zIndex: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  p: { xs: 3, md: 4 },
                  color: "white",
                }}
              >
                <Typography
                  variant="h3"
                  fontWeight={800}
                  sx={{
                    mb: 1,
                    fontSize: { xs: "1.75rem", sm: "2rem", md: "2rem" },
                    lineHeight: 1.2,
                  }}
                >
                  Welcome, {user?.firstName} {user?.lastName}!
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    opacity: 0.9,
                    maxWidth: 520,
                    mb: 2,
                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                  }}
                >
                  Track your profile activity, connect with exhibitors, and
                  manage your meetings here.
                </Typography>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Profile Completion Card */}
        <Grid item xs={12} sm={6} lg={4}>
          <Fade in timeout={700}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                height: "100%",
                border: "1px solid #eef2f6",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0px 12px 24px -8px rgba(99, 102, 241, 0.15)",
                  borderColor: "#6366f1",
                },
                background: "linear-gradient(135deg, #ffffff 0%, #fafcff 100%)",
              }}
            >
              <CardContent
                sx={{
                  textAlign: "center",
                  py: 3,
                  px: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  minHeight: 180,
                }}
              >
                <Box
                  sx={{
                    width: 90,
                    height: 90,
                    mx: "auto",
                    mb: 2,
                    position: "relative",
                  }}
                >
                  <CircularProgressbar
                    strokeWidth={10}
                    value={pcData?.pc ?? 0}
                    text={`${pcData?.pc ?? 0}%`}
                    styles={buildStyles({
                      pathColor:
                        pcData?.pc >= 80
                          ? "#10b981"
                          : pcData?.pc >= 50
                          ? "#f59e0b"
                          : "#6366f1",
                      textColor: "#0f172a",
                      trailColor: "#f1f5f9",
                      textSize: "20px",
                      pathTransitionDuration: 0.5,
                    })}
                  />
                  {pcData?.pc === 100 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        bgcolor: "#10b981",
                        borderRadius: "50%",
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid white",
                      }}
                    >
                      <StarIcon sx={{ fontSize: 14, color: "white" }} />
                    </Box>
                  )}
                </Box>

                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: "#0f172a", mb: 0.5 }}
                >
                  My Profile
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2.5, fontSize: "0.8rem", lineHeight: 1.4 }}
                >
                  {pcData?.pc >= 80
                    ? "🌟 Excellent! Profile is fully optimized"
                    : pcData?.pc >= 50
                    ? "📈 Good progress! Keep improving"
                    : "⚡ Complete your profile for better visibility"}
                </Typography>

                {pcData?.pc < 100 && (
                  <Button
                    component={Link}
                    to="/visitor/profile"
                    variant="contained"
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      px: 3,
                      bgcolor: "#6366f1",
                      boxShadow: "0px 4px 12px rgba(99, 102, 241, 0.3)",
                      "&:hover": {
                        bgcolor: "#4f46e5",
                        boxShadow: "0px 6px 16px rgba(99, 102, 241, 0.4)",
                      },
                    }}
                  >
                    {pcData?.pc >= 50 ? "Continue" : "Get Started"}
                  </Button>
                )}

                {pcData?.pc === 100 && (
                  <Button
                    component={Link}
                    to="/visitor/profile"
                    variant="outlined"
                    size="small"
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      px: 3,
                      borderColor: "#10b981",
                      color: "#10b981",
                      "&:hover": {
                        borderColor: "#059669",
                        bgcolor: "rgba(16, 185, 129, 0.05)",
                      },
                    }}
                  >
                    View Profile
                  </Button>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsConfigs.map((stat, idx) => (
          <Grid item xs={12} sm={4} key={idx}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      {/* Main Content Area */}
      <Grid container spacing={4}>
        {/* Recommendations Section - Full Width */}
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{ borderRadius: 4, border: "1px solid #eef2f6" }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" fontWeight={700} color="#0f172a">
                Recommendations
              </Typography>
            </Box>
            <CardContent sx={{ p: 3, backgroundColor: "#fafcff" }}>
              <RecommendedExhibitors />
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Activity Section - Half Width */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid #eef2f6",
              height: "100%",
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" fontWeight={700} color="#0f172a">
                Notifications
              </Typography>
            </Box>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                <NotificationsWidget />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Meetings Section - Half Width */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid #eef2f6",
              height: "100%",
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" fontWeight={700} color="#0f172a">
                Your Meetings
              </Typography>
              <Button
                component={Link}
                to="meetings"
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#6366f1",
                }}
              >
                See All
              </Button>
            </Box>
            <CardContent sx={{ p: 2, backgroundColor: "#fafcff" }}>
              {meetingsData?.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ py: 4, fontWeight: 500 }}
                >
                  No Meetings Yet
                </Typography>
              )}
              {isMloading && (
                <Skeleton
                  variant="rectangular"
                  height={100}
                  sx={{ borderRadius: 2 }}
                />
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {meetingsData?.slice(0, 2).map((item, i) => (
                  <MeetingCard key={i} onClick={onSelect} meetingItem={item} />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
